import { Input } from 'antd';
import React, {
  FC,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { DocumentsAPI } from 'api/document/document-core';
import { ProjectId, SpaceTabId, TaskId } from 'api/models';
import { TaskSourceType } from 'api/tasks';
import { TaskTable } from 'components/molecules/tasks/task-table';
import { FetchStatus } from 'data/core';
import {
  createDocSectionTask,
  selectActiveDocumentInfo,
  selectTaskSectionIdNameMap,
  updateDocTaskIdNameMap,
} from 'data/documents';
import { useSpaceTab } from 'data/space-tabs/hooks';
import { getActiveTabKey } from 'data/space-tabs/selectors';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { Task } from 'data/tasks';
import { deleteTaskSagaAction } from 'data/tasks/actions';
import { selectTasksByProjectId } from 'data/tasks/selectors';
import { saveTasks } from 'data/tasks/slice';
import { useIsResponsive } from 'hooks/use-media-query';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation, useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import {
  getProjectStatusUpdatesDetailUrl,
  getSpaceTabDetailUrl,
} from 'utils/urls';
import { EditorData } from '../editor-data/editor-data-store';
import styles from './task-section.module.css';

interface DocumentTasksSectionProps {
  resourceSectionId: number;
  editorData: EditorData | undefined;
}

export const DocumentTasksSectionBase: FC<DocumentTasksSectionProps> = ({
  resourceSectionId,
  editorData,
}) => {
  const documentSectionId = editorData?.sectionid;
  const isPrivate = editorData?.isprivate === 'true';

  const dispatch = useDispatch();
  const translate = useTranslate();
  const { documentId, projectId } = useSelector(selectActiveDocumentInfo);
  const map = useSelector(selectTaskSectionIdNameMap);

  const titleDefault = useTranslation('editor.doc_task_section_placeholder');
  const titleFromStore = map[resourceSectionId]?.content || titleDefault;

  const [title, setTitle] = useState(titleFromStore);

  const projectTasks = useSelector(
    selectTasksByProjectId(ProjectId(projectId as number)),
    shallowEqual,
  );

  // TODO: documentTasksFromStore is for not breaking current documents.
  // After a checking we don't have DOCUMENT tasks in any documents FOR A TASK SECTION (DOCUMENT tasks will be present for Document tasks in the tab),
  // we can remove this. `task.source?.sourceType === 'DOCUMENT'` will be false
  // for all new tasks.
  const documentTasksFromStore = useMemo(
    () =>
      projectTasks.filter(
        (task) =>
          task.source?.sourceType === 'DOCUMENT' &&
          task.source?.sourceId === documentId,
      ),
    [documentId, projectTasks],
  );
  const documentTasks = useMemo(
    () =>
      documentTasksFromStore.filter(
        (task) =>
          task.source?.metaData &&
          task.source?.metaData.resourceSectionId === resourceSectionId,
      ),
    [documentTasksFromStore, resourceSectionId],
  );

  // This will get all document section tasks.
  const documentSectionTasks = useMemo(
    () =>
      projectTasks.filter((task) => {
        const source = (task.sources ?? []).find(
          (_source) =>
            _source?.sourceType === 'DOCUMENT_SECTION' &&
            _source?.metaData &&
            _source?.metaData.resourceSectionId === resourceSectionId &&
            _source?.sourceId === Number(documentSectionId),
        );

        return source;
      }),
    [documentSectionId, projectTasks, resourceSectionId],
  );

  const tableRef = useRef<HTMLDivElement>(null);

  const updateIdMap = useCallback(
    (text?: string) => {
      dispatch(
        updateDocTaskIdNameMap({
          documentId: documentId as number,
          projectId: projectId as number,
          resourceSectionName: text || titleDefault,
          resourceSectionId,
        }),
      );
    },
    [dispatch, documentId, projectId, resourceSectionId, titleDefault],
  );

  useEffect(() => {
    if (map[resourceSectionId]) {
      setTitle(map[resourceSectionId]?.content);
    } else {
      // I'm keeping this log here for now, so that I can detect for possible infinite loops.
      // TODO: @asim remove the log on 30 sep 2021.
      logger.log('DocumentTasksSectionBase', {
        map,
        resourceSectionId,
        result: map[resourceSectionId],
      });
      // update task section id map if not available
      updateIdMap();
    }
  }, [map, resourceSectionId, updateIdMap]);

  const activeSpaceTabKey = useSelector(getActiveTabKey);
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const statusUpdateId = activeStatusUpdate?.statusUpdateId;
  const { data: activeSpaceTab } = useSpaceTab(parseInt(activeSpaceTabKey, 10));

  const hostname = window.location.origin;
  const pathName = window.location.pathname;

  const taskDescriptionPostfix = pathName.includes('spaces')
    ? translate('document.tasks.reference_doc_info', {
        a: () => `<a
          href="${hostname}${getSpaceTabDetailUrl(
          SpaceTabId<number>(
            projectId as number,
            activeSpaceTab.space,
            activeSpaceTab.spaceTabId,
          ),
        )}"
        >
          ${activeSpaceTab.spaceTabTitle}
        </a>`,
      })
    : translate('status.update.tasks.reference_doc_info', {
        a: () => `<a
          href="${hostname}${getProjectStatusUpdatesDetailUrl(
          projectId as number,
          statusUpdateId!,
        )}"
        >
          ${activeStatusUpdate?.statusUpdateTitle || 'Untitled'}
        </a>`,
      });

  const handleNewTaskAdded = (taskName: string) => {
    dispatch(
      createDocSectionTask({
        documentId: documentId as number,
        projectId: projectId as number,
        taskName,
        taskDescriptionPostfix,
        meta: {
          resourceSectionId,
          documentSectionId: Number(documentSectionId),
          private: isPrivate,
        },
      }),
    );

    return Promise.resolve();
  };

  const onTitleChange = (text: string) => {
    // Not the best to keep two sources, but redux one is slow to update
    // TODO: Optimise further.
    setTitle(text);
  };

  const onTitleBlur = (text: string) => {
    updateIdMap(text);
  };

  const handleEnter = () => {
    tableRef.current!.focus();
  };

  const documentTitlePlaceholder = useTranslation(
    'editor.doc_task_section_placeholder',
  );
  const newTaskPlaceholder = useTranslation('placeholder.task.new');

  const navigateToTask = useNavigateToTask();

  const handleTaskNameClick = (taskId: TaskId) => {
    navigateToTask(taskId);
  };

  const isResponsive = useIsResponsive();
  const handleDelete = async (task: Task) => {
    const sources = task.sources ?? [];

    const hasDefault = sources.some(
      (s) => s.sourceType === TaskSourceType.Default,
    );
    const hasDocumentSection = sources.some(
      (s) => s.sourceType === TaskSourceType.DocumentSection,
    );

    const isExistingTask = hasDefault && hasDocumentSection;

    if (!projectId) {
      return;
    }

    if (isExistingTask) {
      try {
        const response = await DocumentsAPI.deleteExistingSectionTask(
          projectId,
          {
            taskId: task.taskId,
            taskName: task.taskName,
            source: task.source,
            sources: task.sources,
          },
        );

        dispatch(
          saveTasks({ data: [response], fetchStatus: FetchStatus.Loaded }),
        );
      } catch (error) {
        logger.error('Failed to delete the task in doc section', { error });
      }
    } else {
      dispatch(deleteTaskSagaAction({ projectId, taskId: task.taskId }));
    }
  };

  return (
    <div className={styles.wrapper}>
      <Input
        className={styles.doc_title}
        placeholder={documentTitlePlaceholder}
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        disabled={isResponsive}
        onBlur={(e) => {
          onTitleBlur(e.target.value);
        }}
        onPressEnter={handleEnter}
      />
      {projectId && (
        <TaskTable
          isReadOnly={isResponsive}
          isPrivate={isPrivate}
          onTaskNameClick={handleTaskNameClick}
          projectId={projectId}
          refTo={tableRef}
          data={[...documentSectionTasks, ...documentTasks]}
          handleNewTaskAdded={handleNewTaskAdded}
          newRowPlaceholder={newTaskPlaceholder}
          documentSectionId={Number(documentSectionId)}
          resourceSectionId={resourceSectionId}
          onDelete={handleDelete}
          useClearAction
        />
      )}
    </div>
  );
};
